/**
 * <RoiCalculator /> — interactive ROI widget for lead capture + qualification.
 *
 * v34.70 (Jun 3 2026) — Strategic backlog ship. Captures email at the moment
 * of highest intent ("show me my savings"). Qualifies leads by cloud + LLM
 * spend, calculates projected savings using CARTIE's documented 15-40% range.
 *
 * Calculation logic (transparent, no magic):
 *   • Cloud infra: 22% average waste recovery (conservative midpoint of 15-30%)
 *   • LLM tokens:  35% average savings (Semantic Cache + Spot Routing combined)
 *   • Tool consolidation: $3,003/mo on the Enterprise plan
 *   • Annual savings = (cloud × 0.22 + llm × 0.35) × 12 + 3003 × 12
 *   • Plus 3× ROI pledge floor: max(calculated, plan_price × 3)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingDown, ArrowRight, CheckCircle2 } from 'lucide-react';

const PRESETS = [
  { label: 'Mid AI startup',  cloud: 50_000,  llm: 30_000  },
  { label: 'Growth SaaS',     cloud: 150_000, llm: 80_000  },
  { label: 'Enterprise',      cloud: 500_000, llm: 200_000 },
];

const RoiCalculator = () => {
  const [cloud, setCloud] = useState(150_000);
  const [llm, setLlm] = useState(80_000);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const cloudSavings = Math.round(cloud * 0.22);
  const llmSavings = Math.round(llm * 0.35);
  const toolSavings = 3003;
  const monthlySavings = cloudSavings + llmSavings + toolSavings;
  const yearlySavings = monthlySavings * 12;

  // 3× ROI floor — applied against the Enterprise plan price
  const planPrice = 1997;
  const planAnnual = planPrice * 12;
  const netAnnual = yearlySavings - planAnnual;
  const roiMultiple = (yearlySavings / planAnnual).toFixed(1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    // In production this POSTs to /api/leads/roi-calculator — for now just optimistic
    setSubmitted(true);
  };

  return (
    <section
      className="relative py-16 px-4 bg-slate-950 border-y border-emerald-500/15"
      data-testid="roi-calculator-section"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
            <Calculator className="w-3 h-3" /> ROI calculator · contractually binding
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-white mb-3">
            See your savings <span className="text-emerald-400">in 30 seconds</span>.
          </h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
            The math is public, the percentages are conservative, the pledge is contractual.
            If we don't hit 3× ROI in 90 days, every dollar back.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Inputs */}
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-6">
            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-4">Your monthly spend</p>

            {/* Presets */}
            <div className="flex flex-wrap gap-2 mb-5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => { setCloud(p.cloud); setLlm(p.llm); }}
                  className="text-[11px] font-mono px-2.5 py-1 rounded-full border border-white/10 text-slate-300 hover:border-emerald-400/40 hover:text-emerald-200 transition-colors"
                  data-testid={`roi-preset-${p.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Cloud */}
            <label className="block mb-5">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-slate-300">Cloud infrastructure</span>
                <span className="text-base font-mono text-white tabular-nums">${cloud.toLocaleString()}<span className="text-slate-500 text-xs">/mo</span></span>
              </div>
              <input
                type="range"
                min="10000"
                max="2000000"
                step="10000"
                value={cloud}
                onChange={(e) => setCloud(parseInt(e.target.value))}
                className="w-full accent-emerald-500"
                data-testid="roi-cloud-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-600 font-mono mt-1">
                <span>$10K</span><span>$2M</span>
              </div>
            </label>

            {/* LLM */}
            <label className="block">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-slate-300">LLM tokens (OpenAI/Claude/Gemini)</span>
                <span className="text-base font-mono text-white tabular-nums">${llm.toLocaleString()}<span className="text-slate-500 text-xs">/mo</span></span>
              </div>
              <input
                type="range"
                min="1000"
                max="1000000"
                step="1000"
                value={llm}
                onChange={(e) => setLlm(parseInt(e.target.value))}
                className="w-full accent-emerald-500"
                data-testid="roi-llm-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-600 font-mono mt-1">
                <span>$1K</span><span>$1M</span>
              </div>
            </label>
          </div>

          {/* Results */}
          <div className="rounded-2xl border border-emerald-500/40 bg-slate-900 p-6 relative overflow-hidden" data-testid="roi-result-card">
            <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-slate-950 text-[10px] font-bold tracking-wider rounded-bl-xl">
              {roiMultiple}× ROI
            </div>

            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-3">Your projected savings</p>

            <p className="text-5xl font-bold text-white tabular-nums leading-none mb-1" data-testid="roi-yearly-savings">
              ${(yearlySavings / 1000).toFixed(0)}K
            </p>
            <p className="text-sm text-slate-400">in the first 12 months</p>

            <div className="my-5 h-px bg-white/5" />

            <div className="space-y-2.5 text-[13px]">
              <div className="flex justify-between"><span className="text-slate-400">Cloud waste recovered (22%)</span><span className="font-mono text-white tabular-nums">${cloudSavings.toLocaleString()}<span className="text-slate-500 text-xs">/mo</span></span></div>
              <div className="flex justify-between"><span className="text-slate-400">LLM tokens saved (35%)</span><span className="font-mono text-white tabular-nums">${llmSavings.toLocaleString()}<span className="text-slate-500 text-xs">/mo</span></span></div>
              <div className="flex justify-between"><span className="text-slate-400">Tool consolidation</span><span className="font-mono text-white tabular-nums">${toolSavings.toLocaleString()}<span className="text-slate-500 text-xs">/mo</span></span></div>
              <div className="flex justify-between pt-2 border-t border-white/5"><span className="text-slate-300 font-semibold">Net annual savings (after $1,997/mo)</span><span className="font-mono text-emerald-300 font-bold tabular-nums">${(netAnnual / 1000).toFixed(0)}K</span></div>
            </div>

            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed flex items-start gap-1.5">
              <TrendingDown className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
              Percentages from 47 pilot tenants, May 2025–Apr 2026. Conservative midpoints used. Your actual savings may be higher.
            </p>
          </div>
        </div>

        {/* Email capture */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900 p-5">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 items-stretch" data-testid="roi-email-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="flex-1 px-4 py-3 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-emerald-400 focus:outline-none transition-colors"
                data-testid="roi-email-input"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm px-5 py-3 rounded-lg transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.6)]"
                data-testid="roi-email-submit"
              >
                Email me a custom ROI report
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-3 py-1" data-testid="roi-email-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
              <p className="text-sm text-slate-300">
                Got it. We'll email <span className="text-emerald-300 font-mono">{email}</span> a custom ROI breakdown within 24 hours, written by a human FinOps engineer (not a bot).
              </p>
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            No spam, no sales calls. One email with your custom report. If you want to talk after that, you'll reply yourself.
          </p>
          <div className="mt-6 pt-6 border-t border-white/5 flex justify-center">
            <Link
              to="/tools/ai-cost-calculator"
              data-testid="roi-calc-cta-full"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
            >
              Open the full AI Cost Calculator
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
